/**
 * Mark reviewed: optimistic, with Undo for six seconds in the toast and on the sheet. Nothing is written until
 * the window is up (the toast leaving, or the hold's own clock: a toast kept open under the pointer must not
 * keep the write back), so an Undo leaves nothing in the record; Undo after that deletes the acknowledgement
 * the write returned. A call picked on the sheet rides along in the same write.
 */
import { useCallback, useRef, useState } from "react";
import { isConflict, type ApiError } from "../../api/client";
import { createCaseAcknowledgement, deleteCaseAcknowledgement } from "../../api/outreach";
import { invalidate } from "../data/useQuery";
import { useToast } from "../kit";
import { hold, UNDO_MS, type Held } from "./hold";
import { doneWords, type CaseKind } from "./review";

/** The server refused the review because the person is not on that queue (#604): not a colleague's review, a review that must not stand. */
const notOnQueue = (error: unknown): boolean => isConflict(error) && ((error as ApiError).body as { code?: string } | undefined)?.code === "not_on_queue";

/** Everything that shows a review: the person, People's tables and Today's queues (and so the sidebar's counts). */
function forget(id: number): void {
  invalidate(`v2:person:${id}`);
  invalidate("people:");
  invalidate("v2:today:");
}

interface Pending {
  toast: string;
  held: Held;
}

export function useReview() {
  const toast = useToast();
  const [local, setLocal] = useState<Record<number, boolean>>({});
  /** Reviews waiting out their Undo window, per person. */
  const pending = useRef(new Map<number, Pending>());
  /** The write in flight or done, per person: the acknowledgement's id, or null when someone else's was there first. */
  const writes = useRef(new Map<number, Promise<number | null>>());

  /** `server` is what the API said; a local choice (mark or undo) wins until the page reloads. */
  const isReviewed = (id: number, server: boolean) => local[id] ?? server;

  /** `ackId`: the acknowledgement the person's record already holds, for a review this session did not write. */
  const undo = useCallback(
    (id: number, ackId?: number) => {
      setLocal((s) => ({ ...s, [id]: false }));
      const waiting = pending.current.get(id);
      if (waiting) {
        // Not written yet: forget it, and let its toast go.
        waiting.held.cancel();
        pending.current.delete(id);
        toast.dismiss(waiting.toast);
        return;
      }
      // A write that failed has nothing to take back.
      const written = (writes.current.get(id) ?? Promise.resolve(ackId ?? null)).catch(() => null);
      writes.current.delete(id);
      void written
        .then((known) => (known === null && ackId !== undefined ? ackId : known))
        .then((target) => (target === null ? undefined : deleteCaseAcknowledgement(target)))
        .then(
          () => forget(id),
          () => {
            setLocal((s) => ({ ...s, [id]: true }));
            toast.show({ message: "Could not undo the review" });
          },
        );
    },
    [toast],
  );

  const commit = useCallback(
    (id: number, queueKey: string, call: string | undefined) => {
      pending.current.delete(id);
      const write = createCaseAcknowledgement(id, {
        queue_key: queueKey,
        note: call ?? "Reviewed",
        ...(call && { contact_channel: "call" as const }),
      }).then(
        (ack) => ack.id,
        (error: unknown) => {
          // Someone else got there first: the person is reviewed, which is what was asked.
          if (isConflict(error) && !notOnQueue(error)) return null;
          throw error;
        },
      );
      writes.current.set(id, write);
      void write.then(
        () => forget(id),
        (error: unknown) => {
          writes.current.delete(id);
          setLocal((s) => ({ ...s, [id]: false }));
          toast.show({ message: notOnQueue(error) ? "Not on this list now, so not reviewed" : "Could not save the review" });
          if (notOnQueue(error)) forget(id);
        },
      );
    },
    [toast],
  );

  /** `call`: the summary of a call picked but not yet saved, logged with the review in one toast. */
  const mark = useCallback(
    (id: number, kind: CaseKind, queueKey: string, call?: string) => {
      setLocal((s) => ({ ...s, [id]: true }));
      const done = doneWords(kind).toast;
      const message = call ? `Call logged · ${done.toLowerCase()}` : done;
      const held = hold(() => commit(id, queueKey, call));
      const shown = toast.show({ message, action: { label: "Undo", onAction: () => undo(id) }, duration: UNDO_MS, onExpire: held.flush });
      pending.current.set(id, { toast: shown, held });
    },
    [toast, undo, commit],
  );

  return { isReviewed, mark, undo };
}
